import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    BusinessEntityPicker,
    BusinessPickerField,
    type BusinessEntityPickerConfig,
    type BusinessPickerFieldWrapperProps,
} from "@src/components/business-data-entry";
import { PickerSurface } from "@shared/ui/picker-surface";
import {
    storePlacesApi,
    storePlacesServices,
    type ApiStorePlaceData,
    type StorePlaceRecommendedCell,
} from "../../api/storePlaces";

/** Опция picker ячейки места хранения (временный контракт до карты склада). */
interface StorePlaceCellPickerOption {
    /** Код ячейки. */
    Code: number;
    /** Номер/обозначение ячейки. */
    Num: string;
    /** Индекс места хранения. */
    SPIndex: string;
}

/** Пропсы компонента {@link StorePlaceCellPickerField}. */
export interface StorePlaceCellPickerFieldProps
    extends Omit<BusinessPickerFieldWrapperProps<number>, "value" | "onChange" | "windowTitle" | "summaryFormat"> {
    /** Выбранный код ячейки; `null` — значение не выбрано. */
    value: number | null;
    /**
     * Колбэк изменения выбранной ячейки.
     * @param cellCode код ячейки или `null` после сброса
     */
    onChange?: (cellCode: number | null) => void;
    /**
     * Код склада ПО / документа. \
     * Опции грузятся через {@link storePlacesServices.loadAvailableCellsForPlacement}
     * (`DivisionCode` + `OnlyEndPoints: true`).
     */
    divisionCode: number;
    /**
     * Рекомендованные ячейки строки. \
     * Показываются в начале списка; не ограничивают выбор — можно взять любую конечную ячейку склада.
     */
    recommendedCells?: StorePlaceRecommendedCell[];
    /**
     * Заголовок overlay-окна.
     * @default "Выбор ячейки размещения"
     */
    windowTitle?: string;
}

/**
 * Picker-поле выбора ячейки размещения.
 *
 * Открывает fullscreen app window с entity-list; позже будет заменён на карту склада. \
 * Источник опций — {@link storePlacesServices.loadAvailableCellsForPlacement}; рекомендации — в начале списка. \
 * Single select с немедленным commit при выборе.
 */
export const StorePlaceCellPickerField: React.FC<StorePlaceCellPickerFieldProps> = ({
    value,
    onChange,
    divisionCode,
    recommendedCells,
    label = "Ячейка размещения",
    required = false,
    error,
    disabled = false,
    placeholder,
    windowTitle = "Выбор ячейки размещения",
}) => {
    const selectedIds = value == null ? [] : [value];
    const [cellsByCode, setCellsByCode] = useState(() => new Map<number, StorePlaceCellPickerOption>());
    const cellsByCodeRef = useRef(cellsByCode);
    cellsByCodeRef.current = cellsByCode;

    const recommendedOptions = useMemo(() => (recommendedCells ?? []).map(toPickerOption), [recommendedCells]);

    const recommendedCodesKey = useMemo(() => recommendedOptions.map((cell) => cell.Code).join(","), [recommendedOptions]);

    useEffect(() => {
        if (recommendedOptions.length === 0) {
            return;
        }

        setCellsByCode((prev) => mergeCellsIntoMap(prev, recommendedOptions));
    }, [recommendedOptions]);

    useEffect(() => {
        if (value == null || cellsByCodeRef.current.has(value)) {
            return;
        }

        let cancelled = false;

        void storePlacesApi.load({ filter: { Codes: [value] } }).then((items) => {
            if (cancelled || items.length === 0) {
                return;
            }

            setCellsByCode((prev) => mergeCellsIntoMap(prev, items.map(toPickerOptionFromStorePlace)));
        });

        return () => {
            cancelled = true;
        };
    }, [value]);

    const picker = useMemo((): BusinessEntityPickerConfig<StorePlaceCellPickerOption, number> => {
        return {
            queryKeyBase: ["stock", "placement", "cellPicker", divisionCode, "endPoints", recommendedCodesKey],
            minSearchLength: 0,
            debounceDelay: 200,
            idleText: "Конечные ячейки склада. Рекомендованные — в начале списка.",
            emptyText: "Ячейки не найдены",
            getId: (cell) => cell.Code,
            getOptionLabel: formatCellLabel,
            loadOptions: async (query, signal) => {
                if (divisionCode <= 0) {
                    return [];
                }

                const loaded = await storePlacesServices.loadAvailableCellsForPlacement({ divisionCode }, { signal });

                const options = mergeRecommendedFirst(recommendedOptions, loaded.map(toPickerOptionFromStorePlace));
                setCellsByCode((prev) => mergeCellsIntoMap(prev, options));

                const normalizedQuery = query.trim().toLowerCase();
                if (normalizedQuery === "") {
                    return options;
                }

                return options.filter(
                    (cell) =>
                        cell.Num.toLowerCase().includes(normalizedQuery) ||
                        cell.SPIndex.toLowerCase().includes(normalizedQuery),
                );
            },
        };
    }, [divisionCode, recommendedCodesKey, recommendedOptions]);

    const summaryFormat = useCallback(
        (count: number): string => {
            if (count === 0) {
                return "";
            }

            const selectedCode = value;
            if (selectedCode == null) {
                return "";
            }

            const cell = cellsByCode.get(selectedCode);
            if (cell != null) {
                return formatCellLabel(cell);
            }

            return `Код: ${selectedCode}`;
        },
        [cellsByCode, value],
    );

    const handleChange = useCallback(
        (ids: number[]) => {
            onChange?.(ids[0] ?? null);
        },
        [onChange],
    );

    return (
        <BusinessPickerField<number>
            label={label}
            required={required}
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            windowTitle={windowTitle}
            windowKey="stock.placement.cellPicker"
            value={selectedIds}
            onChange={handleChange}
            summaryFormat={summaryFormat}
            showConfirmFooter={false}
            renderWindowContent={({ value: draftIds, submit, close, readonly }) => (
                <BusinessEntityPicker
                    config={picker}
                    value={draftIds}
                    onChange={(ids) => {
                        if (ids.length > 0) {
                            submit(ids);
                        }
                    }}
                    selectionMode="single"
                    disabled={readonly}
                    footerSlot={<PickerSurface.CloseButton onClick={close}>Вернуться назад</PickerSurface.CloseButton>}
                />
            )}
        />
    );
};

/**
 * Рекомендованные ячейки первыми, остальные конечные — следом без дублей.
 * @param recommended рекомендованные опции (порядок сохраняется)
 * @param loaded загруженные конечные ячейки склада
 * @returns объединённый список для picker
 */
function mergeRecommendedFirst(
    recommended: StorePlaceCellPickerOption[],
    loaded: StorePlaceCellPickerOption[],
): StorePlaceCellPickerOption[] {
    const seen = new Set<number>();
    const result: StorePlaceCellPickerOption[] = [];

    for (const cell of recommended) {
        if (seen.has(cell.Code)) {
            continue;
        }

        seen.add(cell.Code);
        result.push(cell);
    }

    for (const cell of loaded) {
        if (seen.has(cell.Code)) {
            continue;
        }

        seen.add(cell.Code);
        result.push(cell);
    }

    return result;
}

/**
 * Дописывает опции в map без потери уже известных ячеек.
 * @param prev текущий map
 * @param cells новые опции
 * @returns новый map
 */
function mergeCellsIntoMap(
    prev: Map<number, StorePlaceCellPickerOption>,
    cells: StorePlaceCellPickerOption[],
): Map<number, StorePlaceCellPickerOption> {
    const next = new Map(prev);
    cells.forEach((cell) => {
        next.set(cell.Code, cell);
    });
    return next;
}

function toPickerOption(cell: StorePlaceRecommendedCell): StorePlaceCellPickerOption {
    return {
        Code: cell.Code,
        Num: cell.Num,
        SPIndex: cell.SPIndex,
    };
}

function toPickerOptionFromStorePlace(cell: ApiStorePlaceData): StorePlaceCellPickerOption {
    return {
        Code: cell.Code,
        Num: cell.Num,
        SPIndex: cell.SPIndex,
    };
}

function formatCellLabel(cell: StorePlaceCellPickerOption): string {
    if (cell.SPIndex === "") {
        return cell.Num;
    }

    return `${cell.Num} — ${cell.SPIndex}`;
}
